import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './hooks/useAuth';
import Layout from './components/layout/Layout';
import { isGeneAnalysisPublicEnabled, isKeisukePublicEnabled, isLaboSukePublicEnabled, isSamplePublicEnabled, isTppPublicEnabled } from './utils/config';
import { lazyWithChunkReload } from './utils/lazyWithChunkReload';
import { isPublicDemoAccessPath } from './utils/publicAccessPaths';
import { ProtectedDevFeatureRoute } from './components/ProtectedDevFeatureRoute';
import { ProtectedAdminRoute } from './components/ProtectedAdminRoute';
import SampleZipModeShell from './components/samples/SampleZipModeShell';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import SampleDashboard from './pages/SampleDashboard';
import SampleManager from './pages/SampleManager';
import SampleCaseManager from './pages/SampleCaseManager';
import SampleDetail from './pages/SampleDetail';
import UsageHistory from './pages/UsageHistory';
import UsageRecords from './pages/UsageRecords';
import {
  LegacySampleDetailRedirect,
  LegacySampleUsageHistoryRedirect,
} from './components/samples/LegacySampleRouteRedirects';
import UsageRecord from './pages/UsageRecord';
import BillingManager from './pages/BillingManager';
import Profile from './pages/Profile';
import StorageRoomsManager from './pages/StorageRoomsManager';
import Contact from './pages/Contact';
import ExcelImport from './pages/ExcelImport';
import ExcelExport from './pages/ExcelExport';
import Reports from './pages/Reports';
import UsersManager from './pages/UsersManager';
import AdminSettings from './pages/AdminSettings';
import AccessLogs from './pages/AccessLogs';
import AdminInsightsLayout, { HostSecurityPanel } from './pages/AdminInsights';
import ConversionFunnel from './pages/ConversionFunnel';
import Announcements from './pages/Announcements';
import AdminAnnouncements from './pages/AdminAnnouncements';
import UserActivityLogs from './pages/UserActivityLogs';
import DatabaseBackupRestore from './pages/DatabaseBackupRestore';
import PasswordReset from './pages/PasswordReset';
// PasswordResetコンポーネントの動的インポート（Viteキャッシュ問題の回避）
import Help from './pages/Help';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import ServerError from './pages/ServerError';
import GeneAnalysisManager from './pages/GeneAnalysisManager';
import ReservationBoardsManager from './pages/ReservationBoardsManager';
import ReservationBoardCalendar from './pages/ReservationBoardCalendar';
import ReservationBoardLegacyRedirect from './pages/ReservationBoardLegacyRedirect';
import TransitPassPool from './pages/TransitPassPool';
import KeisukeManager from './pages/KeisukeManager';
import PublicSampleDetail from './pages/PublicSampleDetail';
import PublicLocationShare from './pages/PublicLocationShare';
import UnauthenticatedCatchAll from './components/UnauthenticatedCatchAll';
import TikTokOAuthCallback from './pages/TikTokOAuthCallback';
import { localeFromPublicPathname, SUPPORTED_LOCALES } from './i18n/config';

const LaboSukeManager = lazyWithChunkReload(() => import('./pages/LaboSukeManager'));
const GaisukeManager = lazyWithChunkReload(() => import('./pages/GaisukeManager'));
const GaisukeArenaSettingsPage = lazyWithChunkReload(() => import('./pages/GaisukeArenaSettingsPage'));
const GaisukeArenaWeeklySummaryRoute = lazyWithChunkReload(
  () => import('./pages/GaisukeArenaWeeklySummaryRoute'),
);
const GaisukeArenaFaqPage = lazyWithChunkReload(() => import('./pages/GaisukeArenaFaqPage'));
const KeibaArenaManager = lazyWithChunkReload(() => import('./pages/KeibaArenaManager'));
const KeibaArenaAkioInputPage = lazyWithChunkReload(
  () => import('./pages/KeibaArenaAkioInputPage')
);
const BanquetPublicPage = lazyWithChunkReload(() => import('./pages/BanquetPublicPage'));
const EconReportPublicPage = lazyWithChunkReload(() => import('./pages/EconReportPublicPage'));
const EconReportComposePage = lazyWithChunkReload(() => import('./pages/EconReportComposePage'));
const GaisukeFxPublicPage = lazyWithChunkReload(() => import('./pages/GaisukeFxPublicPage'));

/**
 * トップページ (/) と各言語ページ (/en, /ja, ...) は必ず同じ Home コンポーネントで描画する。
 * 言語別のランディング用コンポーネントを新設しないこと。同一性の担保はこのルート定義に依存する。
 */
const NON_JA_LOCALES = SUPPORTED_LOCALES.filter((locale) => locale !== 'ja');

const LOCALE_HOME_ROUTES = [
  <Route key="ja" path="/ja" element={<Navigate to="/" replace />} />,
  ...NON_JA_LOCALES.map((locale) => (
    <Route key={locale} path={`/${locale}`} element={<Home />} />
  )),
];

const helpPageElement = (
  <Layout>
    <Help />
  </Layout>
);

const LOCALE_HELP_ROUTES = [
  <Route key="help-ja" path="/ja/help" element={<Navigate to="/help" replace />} />,
  ...NON_JA_LOCALES.map((locale) => (
    <Route key={`help-${locale}`} path={`/${locale}/help`} element={helpPageElement} />
  )),
];

const contactPageElement = (
  <Layout>
    <Contact />
  </Layout>
);

const LOCALE_CONTACT_ROUTES = [
  <Route key="contact-ja" path="/ja/contact" element={<Navigate to="/contact" replace />} />,
  ...NON_JA_LOCALES.map((locale) => (
    <Route key={`contact-${locale}`} path={`/${locale}/contact`} element={contactPageElement} />
  )),
];

const announcementsPageElement = (
  <Layout>
    <Announcements />
  </Layout>
);

const LOCALE_ANNOUNCEMENTS_ROUTES = [
  <Route key="announcements-ja" path="/ja/announcements" element={<Navigate to="/announcements" replace />} />,
  ...NON_JA_LOCALES.map((locale) => (
    <Route
      key={`announcements-${locale}`}
      path={`/${locale}/announcements`}
      element={announcementsPageElement}
    />
  )),
];

const gaisukePageElement = (
  <Layout>
    <Suspense fallback={<div className="p-4 text-center">Loading…</div>}>
      <GaisukeManager />
    </Suspense>
  </Layout>
);

const LOCALE_GAISUKE_ROUTES = [
  <Route key="gaisuke-ja" path="/ja/gaisuke" element={<Navigate to="/gaisuke" replace />} />,
  ...NON_JA_LOCALES.map((locale) => (
    <Route key={`gaisuke-${locale}`} path={`/${locale}/gaisuke`} element={gaisukePageElement} />
  )),
];

const keibaArenaPageElement = (
  <Layout>
    <Suspense fallback={<div className="p-4 text-center">Loading…</div>}>
      <KeibaArenaManager />
    </Suspense>
  </Layout>
);

const LOCALE_KEIBA_ARENA_ROUTES = [
  <Route
    key="keiba-arena-ja"
    path="/ja/keiba-arena"
    element={<Navigate to="/arena/keiba" replace />}
  />,
  ...NON_JA_LOCALES.map((locale) => (
    <Route
      key={`keiba-arena-${locale}`}
      path={`/${locale}/keiba-arena`}
      element={<Navigate to={`/${locale}/arena/keiba`} replace />}
    />
  )),
];

const LOCALE_ARENA_KEIBA_ROUTES = [
  <Route
    key="arena-keiba-ja"
    path="/ja/arena/keiba"
    element={<Navigate to="/arena/keiba" replace />}
  />,
  ...NON_JA_LOCALES.map((locale) => (
    <Route
      key={`arena-keiba-${locale}`}
      path={`/${locale}/arena/keiba`}
      element={keibaArenaPageElement}
    />
  )),
];

const banquetPageElement = (
  <Layout>
    <Suspense fallback={<div className="p-4 text-center">Loading…</div>}>
      <BanquetPublicPage />
    </Suspense>
  </Layout>
);

const LOCALE_ARENA_BANQUET_ROUTES = [
  <Route
    key="arena-banquet-ja"
    path="/ja/arena/banquet"
    element={<Navigate to="/arena/banquet" replace />}
  />,
  <Route
    key="arena-banquet-ja-seg"
    path="/ja/arena/banquet/:segment"
    element={banquetPageElement}
  />,
  ...NON_JA_LOCALES.map((locale) => (
    <Route
      key={`arena-banquet-${locale}`}
      path={`/${locale}/arena/banquet`}
      element={banquetPageElement}
    />
  )),
  ...NON_JA_LOCALES.map((locale) => (
    <Route
      key={`arena-banquet-${locale}-seg`}
      path={`/${locale}/arena/banquet/:segment`}
      element={banquetPageElement}
    />
  )),
];

const econReportPageElement = (
  <Layout>
    <Suspense fallback={<div className="p-4 text-center">Loading…</div>}>
      <EconReportPublicPage />
    </Suspense>
  </Layout>
);

const gaisukeFxPageElement = (
  <Layout>
    <Suspense fallback={<div className="p-4 text-center">Loading…</div>}>
      <GaisukeFxPublicPage />
    </Suspense>
  </Layout>
);

const LOCALE_ARENA_FX_ROUTES = [
  <Route
    key="arena-fx-ja"
    path="/ja/arena/fx"
    element={<Navigate to="/arena/fx" replace />}
  />,
  ...NON_JA_LOCALES.map((locale) => (
    <Route
      key={`arena-fx-${locale}`}
      path={`/${locale}/arena/fx`}
      element={gaisukeFxPageElement}
    />
  )),
];

const LOCALE_ARENA_ECON_ROUTES = [
  <Route
    key="arena-econ-ja"
    path="/ja/arena/econ"
    element={<Navigate to="/arena/econ" replace />}
  />,
  <Route key="arena-econ-ja-slug" path="/ja/arena/econ/:slug" element={econReportPageElement} />,
  ...NON_JA_LOCALES.map((locale) => (
    <Route
      key={`arena-econ-${locale}`}
      path={`/${locale}/arena/econ`}
      element={econReportPageElement}
    />
  )),
  ...NON_JA_LOCALES.map((locale) => (
    <Route
      key={`arena-econ-${locale}-slug`}
      path={`/${locale}/arena/econ/:slug`}
      element={econReportPageElement}
    />
  )),
];

const gaisukeWeeklyPageElement = (
  <Layout>
    <Suspense fallback={<div className="p-4 text-center">Loading…</div>}>
      <GaisukeArenaWeeklySummaryRoute />
    </Suspense>
  </Layout>
);

function RedirectJaGaisukeWeekly() {
  const { weekStart } = useParams();
  return <Navigate to={`/gaisuke/weekly/${weekStart ?? ''}`} replace />;
}

const LOCALE_GAISUKE_WEEKLY_ROUTES = [
  <Route
    key="gaisuke-weekly-ja"
    path="/ja/gaisuke/weekly/:weekStart"
    element={<RedirectJaGaisukeWeekly />}
  />,
  ...NON_JA_LOCALES.map((locale) => (
    <Route
      key={`gaisuke-weekly-${locale}`}
      path={`/${locale}/gaisuke/weekly/:weekStart`}
      element={gaisukeWeeklyPageElement}
    />
  )),
];

const gaisukeFaqPageElement = (
  <Layout>
    <Suspense fallback={<div className="p-4 text-center">Loading…</div>}>
      <GaisukeArenaFaqPage />
    </Suspense>
  </Layout>
);

const LOCALE_GAISUKE_FAQ_ROUTES = [
  <Route
    key="gaisuke-faq-ja"
    path="/ja/gaisuke/faq"
    element={<Navigate to="/gaisuke/faq" replace />}
  />,
  ...NON_JA_LOCALES.map((locale) => (
    <Route
      key={`gaisuke-faq-${locale}`}
      path={`/${locale}/gaisuke/faq`}
      element={gaisukeFaqPageElement}
    />
  )),
];

const laboSukePageElement = (
  <Layout>
    <Suspense fallback={<div className="p-4 text-center">Loading…</div>}>
      <LaboSukeManager />
    </Suspense>
  </Layout>
);

const LOCALE_LABO_SUKE_ROUTES = [
  <Route key="labo-suke-ja" path="/ja/labo-suke" element={<Navigate to="/labo-suke" replace />} />,
  ...NON_JA_LOCALES.map((locale) => (
    <Route key={`labo-suke-${locale}`} path={`/${locale}/labo-suke`} element={laboSukePageElement} />
  )),
];

const keisukePageElement = (
  <Layout>
    <KeisukeManager />
  </Layout>
);

function RedirectJaKeisuke() {
  const params = useParams();
  const rest = params['*'];
  return <Navigate to={rest ? `/keisuke/${rest}` : '/keisuke'} replace />;
}

const LOCALE_KEISUKE_ROUTES = [
  <Route key="keisuke-ja" path="/ja/keisuke/*" element={<RedirectJaKeisuke />} />,
  ...NON_JA_LOCALES.map((locale) => (
    <Route key={`keisuke-${locale}`} path={`/${locale}/keisuke/*`} element={keisukePageElement} />
  )),
];

const geneAnalysisPageElement = (
  <Layout>
    <GeneAnalysisManager />
  </Layout>
);

function RedirectJaGeneAnalysis() {
  const params = useParams();
  const rest = params['*'];
  return <Navigate to={rest ? `/gene-analysis/${rest}` : '/gene-analysis'} replace />;
}

function App() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const geneAnalysisPublic = isGeneAnalysisPublicEnabled();
  const laboSukePublic = isLaboSukePublicEnabled();
  const keisukePublic = isKeisukePublicEnabled();
  const tppPublic = isTppPublicEnabled();
  const samplePublic = isSamplePublicEnabled();

  const sampleZipLayout = (
    <Layout>
      <SampleZipModeShell />
    </Layout>
  );
  // 公開 URL のロケールを正本にする（Googlebot の navigator=en で /gaisuke が英語化するのを防ぐ）
  useEffect(() => {
    const fromPath = localeFromPublicPathname(location.pathname);
    if (fromPath && fromPath !== i18n.language) {
      void i18n.changeLanguage(fromPath);
    }
  }, [location.pathname, i18n.language, i18n]);

  // 言語変更時にHTMLのlang属性を更新（datetime-localなどのネイティブUIの言語を制御）
  useEffect(() => {
    const updateHtmlLang = () => {
      const lang = i18n.language;
      // 言語コードをHTMLのlang属性に適した形式に変換
      // zh-CN -> zh-CN, その他はそのまま
      document.documentElement.lang = lang;
    };

    // 初回設定
    updateHtmlLang();

    // 言語変更を監視
    i18n.on('languageChanged', updateHtmlLang);

    return () => {
      i18n.off('languageChanged', updateHtmlLang);
    };
  }, [i18n]);

  // 認証状態の確認中はローディング（公開デモページは先に描画）
  if (isLoading && !isPublicDemoAccessPath(window.location.pathname)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">
            {t('common.loading', { defaultValue: '読み込み中...' })}
          </span>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* パブリックルート（認証不要） */}
      <Route path="/" element={<Home />} />
      {/* 各言語トップ: 上記 Home と同じコンポーネント。path 検出で i18n が切り替わる。 */}
      {LOCALE_HOME_ROUTES}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      {/* TikTok OAuth Redirect URI（code 表示専用・認証不要） */}
      <Route path="/oauth/tiktok/callback" element={<TikTokOAuthCallback />} />
      <Route
        path="/password-reset"
        element={
          <Layout>
            <PasswordReset />
          </Layout>
        }
      />
      <Route path="/help" element={helpPageElement} />
      {LOCALE_HELP_ROUTES}
      <Route path="/ja/privacy" element={<Navigate to="/privacy" replace />} />
      {NON_JA_LOCALES.map((locale) => (
        <Route
          key={`privacy-${locale}`}
          path={`/${locale}/privacy`}
          element={<Navigate to="/privacy" replace />}
        />
      ))}
      <Route
        path="/privacy"
        element={
          <Layout>
            <Privacy />
          </Layout>
        }
      />
      <Route path="/ja/terms" element={<Navigate to="/terms" replace />} />
      {NON_JA_LOCALES.map((locale) => (
        <Route
          key={`terms-${locale}`}
          path={`/${locale}/terms`}
          element={<Navigate to="/terms" replace />}
        />
      ))}
      <Route
        path="/terms"
        element={
          <Layout>
            <Terms />
          </Layout>
        }
      />
      <Route
        path="/500"
        element={
          <Layout>
            <ServerError />
          </Layout>
        }
      />
      <Route path="/contact" element={contactPageElement} />
      {LOCALE_CONTACT_ROUTES}
      <Route path="/announcements" element={announcementsPageElement} />
      {LOCALE_ANNOUNCEMENTS_ROUTES}
      <Route path="/feature-requests" element={<Navigate to="/contact" replace />} />
      <Route path="/usage-record" element={<UsageRecord />} />
      <Route
        path="/reservations/share/:shareToken"
        element={
          <Layout>
            <ReservationBoardCalendar />
          </Layout>
        }
      />
      <Route path="/gaisuke" element={gaisukePageElement} />
      <Route path="/gaisuke/weekly/:weekStart" element={gaisukeWeeklyPageElement} />
      <Route path="/gaisuke/faq" element={gaisukeFaqPageElement} />
      <Route path="/keiba-arena" element={<Navigate to="/arena/keiba" replace />} />
      <Route
        path="/arena/keiba/akio/:token"
        element={
          <Layout>
            <Suspense fallback={<div className="p-4 text-center">Loading…</div>}>
              <KeibaArenaAkioInputPage />
            </Suspense>
          </Layout>
        }
      />
      <Route path="/arena/keiba" element={keibaArenaPageElement} />
      <Route path="/arena/banquet" element={banquetPageElement} />
      <Route path="/arena/banquet/:segment" element={banquetPageElement} />
      <Route path="/arena/econ" element={econReportPageElement} />
      <Route
        path="/arena/econ/compose"
        element={
          <ProtectedAdminRoute>
            <Layout>
              <Suspense fallback={<div className="p-4 text-center">Loading…</div>}>
                <EconReportComposePage />
              </Suspense>
            </Layout>
          </ProtectedAdminRoute>
        }
      />
      <Route path="/arena/econ/:slug" element={econReportPageElement} />
      <Route path="/arena/fx" element={gaisukeFxPageElement} />
      {LOCALE_GAISUKE_ROUTES}
      {LOCALE_KEIBA_ARENA_ROUTES}
      {LOCALE_ARENA_KEIBA_ROUTES}
      {LOCALE_ARENA_BANQUET_ROUTES}
      {LOCALE_ARENA_ECON_ROUTES}
      {LOCALE_ARENA_FX_ROUTES}
      {LOCALE_GAISUKE_WEEKLY_ROUTES}
      {LOCALE_GAISUKE_FAQ_ROUTES}
      {laboSukePublic && <Route path="/labo-suke" element={laboSukePageElement} />}
      {laboSukePublic && LOCALE_LABO_SUKE_ROUTES}
      {keisukePublic && <Route path="/keisuke/*" element={keisukePageElement} />}
      {keisukePublic && LOCALE_KEISUKE_ROUTES}
      {geneAnalysisPublic && <Route path="/gene-analysis/*" element={geneAnalysisPageElement} />}
      {geneAnalysisPublic && (
        <Route path="/ja/gene-analysis/*" element={<RedirectJaGeneAnalysis />} />
      )}
      {tppPublic && (
        <Route
          path="/transit-pass-pool"
          element={
            <Layout>
              <TransitPassPool />
            </Layout>
          }
        />
      )}
      {tppPublic && (
        <Route path="/ja/transit-pass-pool/*" element={<Navigate to="/transit-pass-pool" replace />} />
      )}
      <Route path="/transit-pass-pool/guest/*" element={<Navigate to="/transit-pass-pool" replace />} />
      <Route path="/transit-pass-pool/return" element={<Navigate to="/transit-pass-pool" replace />} />
      {/* パブリックアクセス用サンプル詳細・場所共有（認証不要） */}
      <Route path="/samples/encrypted/:encryptedId" element={<PublicSampleDetail />} />
      <Route path="/samples/share/:token" element={<PublicLocationShare />} />
      {samplePublic && (
        <Route element={sampleZipLayout}>
          <Route path="/samples/dashboard" element={<SampleDashboard />} />
          <Route path="/samples/cases" element={<SampleCaseManager />} />
          <Route path="/samples/detail" element={<SampleDetail />} />
          <Route path="/samples/usage-history" element={<UsageHistory />} />
          <Route path="/samples/usage-records" element={<UsageRecords />} />
          <Route path="/samples" element={<SampleManager />} />
          <Route path="/samples/:id/usage-history" element={<LegacySampleUsageHistoryRedirect />} />
          <Route path="/samples/:id" element={<LegacySampleDetailRedirect />} />
          <Route path="/storage-rooms" element={<StorageRoomsManager />} />
          <Route
            path="/reports"
            element={
              <ProtectedAdminRoute>
                <Reports />
              </ProtectedAdminRoute>
            }
          />
        </Route>
      )}
      {/* 認証が必要なルート */}
      <Route
        path="/*"
        element={
          isAuthenticated ? (
            <Layout>
              <Routes>
                <Route path="/dashboard" element={<Dashboard />} />
                {!samplePublic && (
                  <Route element={<SampleZipModeShell />}>
                    <Route path="/samples/dashboard" element={<SampleDashboard />} />
                    <Route path="/samples/cases" element={<SampleCaseManager />} />
                    <Route path="/samples/detail" element={<SampleDetail />} />
                    <Route path="/samples/usage-history" element={<UsageHistory />} />
                    <Route path="/samples/usage-records" element={<UsageRecords />} />
                    <Route path="/samples" element={<SampleManager />} />
                    <Route
                      path="/samples/:id/usage-history"
                      element={<LegacySampleUsageHistoryRedirect />}
                    />
                    <Route path="/samples/:id" element={<LegacySampleDetailRedirect />} />
                    <Route path="/storage-rooms" element={<StorageRoomsManager />} />
                    <Route
                      path="/reports"
                      element={
                        <ProtectedAdminRoute>
                          <Reports />
                        </ProtectedAdminRoute>
                      }
                    />
                  </Route>
                )}
                <Route
                  path="/billing/*"
                  element={
                    <ProtectedAdminRoute>
                      <BillingManager />
                    </ProtectedAdminRoute>
                  }
                />
                <Route path="/excel-import" element={<ExcelImport />} />
                <Route path="/excel-export" element={<ExcelExport />} />
                <Route
                  path="/users"
                  element={
                    <ProtectedAdminRoute>
                      <UsersManager />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedAdminRoute>
                      <AdminSettings />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/admin/insights"
                  element={
                    <ProtectedAdminRoute>
                      <AdminInsightsLayout />
                    </ProtectedAdminRoute>
                  }
                >
                  <Route index element={<Navigate to="defense" replace />} />
                  <Route path="defense" element={<HostSecurityPanel />} />
                  <Route path="access-logs" element={<AccessLogs />} />
                  <Route path="conversion" element={<ConversionFunnel />} />
                  <Route path="user-activity" element={<UserActivityLogs />} />
                </Route>
                <Route path="/admin/access-logs" element={<Navigate to="/admin/insights/access-logs" replace />} />
                <Route path="/admin/conversion" element={<Navigate to="/admin/insights/conversion" replace />} />
                <Route path="/admin/user-activity" element={<Navigate to="/admin/insights/user-activity" replace />} />
                <Route
                  path="/admin/database-backup"
                  element={
                    <ProtectedAdminRoute>
                      <DatabaseBackupRestore />
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/admin/announcements"
                  element={
                    <ProtectedAdminRoute>
                      <AdminAnnouncements />
                    </ProtectedAdminRoute>
                  }
                />
                <Route path="/profile" element={<Profile />} />
                {!geneAnalysisPublic && (
                  <Route
                    path="/gene-analysis/*"
                    element={
                      <ProtectedDevFeatureRoute publicEnabled={false}>
                        <GeneAnalysisManager />
                      </ProtectedDevFeatureRoute>
                    }
                  />
                )}
                {!laboSukePublic && (
                <Route
                  path="/labo-suke"
                  element={
                    <ProtectedDevFeatureRoute publicEnabled={false}>
                      <Suspense fallback={<div className="p-4 text-center">Loading…</div>}>
                        <LaboSukeManager />
                      </Suspense>
                    </ProtectedDevFeatureRoute>
                  }
                />
                )}
                {!keisukePublic && <Route path="/keisuke/*" element={<KeisukeManager />} />}
                <Route
                  path="/gaisuke/settings"
                  element={
                    <ProtectedAdminRoute>
                      <Suspense fallback={<div className="p-4 text-center">Loading…</div>}>
                        <GaisukeArenaSettingsPage />
                      </Suspense>
                    </ProtectedAdminRoute>
                  }
                />
                <Route path="/reservations" element={<ReservationBoardsManager />} />
                <Route path="/reservations/:boardId" element={<ReservationBoardLegacyRedirect />} />
                {!tppPublic && (
                  <Route path="/transit-pass-pool" element={<TransitPassPool />} />
                )}
                <Route path="/settings" element={<Navigate to="/profile" replace />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Layout>
          ) : (
            <UnauthenticatedCatchAll />
          )
        }
      />
    </Routes>
  );
}

export default App;
